import api from '../lib/api';
import type { Paginated } from '../types';
import type {
  SalesLog,
  SalesLogClient,
  SalesLogClientInput,
  SalesLogInput,
  SalesLogProduct,
  SalesLogProductInput,
} from '../types/crm';

// Single-record endpoints (show/create/update + nested clients/products)
// return a JSON:API envelope: `{ data: { id, type, attributes: T } }`. List
// endpoints return flat records directly inside `data`. Mirrors the pattern in
// operator.service.ts.

export const salesLogService = {
  getAll: async (params?: Record<string, unknown>): Promise<Paginated<SalesLog>> => {
    const response = await api.get<Paginated<SalesLog>>('/sales_logs', { params });
    return response.data;
  },

  getById: async (id: number): Promise<SalesLog> => {
    const response = await api.get<{ data: { attributes: SalesLog } }>(`/sales_logs/${id}`);
    return response.data.data.attributes;
  },

  create: async (data: SalesLogInput): Promise<SalesLog> => {
    const response = await api.post<{ data: { attributes: SalesLog } }>('/sales_logs', {
      sales_log: data,
    });
    return response.data.data.attributes;
  },

  update: async (id: number, data: Partial<SalesLogInput>): Promise<SalesLog> => {
    const response = await api.patch<{ data: { attributes: SalesLog } }>(`/sales_logs/${id}`, {
      sales_log: data,
    });
    return response.data.data.attributes;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/sales_logs/${id}`);
  },

  // Nested named clients
  addClient: async (
    salesLogId: number,
    data: SalesLogClientInput,
  ): Promise<SalesLogClient> => {
    const response = await api.post<{ data: { attributes: SalesLogClient } }>(
      `/sales_logs/${salesLogId}/clients`,
      { sales_log_client: data },
    );
    return response.data.data.attributes;
  },

  updateClient: async (
    salesLogId: number,
    clientId: number,
    data: Partial<SalesLogClientInput>,
  ): Promise<SalesLogClient> => {
    const response = await api.patch<{ data: { attributes: SalesLogClient } }>(
      `/sales_logs/${salesLogId}/clients/${clientId}`,
      { sales_log_client: data },
    );
    return response.data.data.attributes;
  },

  removeClient: async (salesLogId: number, clientId: number): Promise<void> => {
    await api.delete(`/sales_logs/${salesLogId}/clients/${clientId}`);
  },

  // Nested product line items
  addProduct: async (
    salesLogId: number,
    data: SalesLogProductInput,
  ): Promise<SalesLogProduct> => {
    const response = await api.post<{ data: { attributes: SalesLogProduct } }>(
      `/sales_logs/${salesLogId}/products`,
      { sales_log_product: data },
    );
    return response.data.data.attributes;
  },

  updateProduct: async (
    salesLogId: number,
    productLineId: number,
    data: Partial<SalesLogProductInput>,
  ): Promise<SalesLogProduct> => {
    const response = await api.patch<{ data: { attributes: SalesLogProduct } }>(
      `/sales_logs/${salesLogId}/products/${productLineId}`,
      { sales_log_product: data },
    );
    return response.data.data.attributes;
  },

  removeProduct: async (salesLogId: number, productLineId: number): Promise<void> => {
    await api.delete(`/sales_logs/${salesLogId}/products/${productLineId}`);
  },
};

export default salesLogService;
